import React from 'react';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import { formatRelativeTime } from '../utils/dateFormatter';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, RefreshCw, File, Clock } from 'lucide-react';

interface TrainPagesDialogProps {
    open: boolean;
    websiteId: string;
    pages: any[];
    onClose: () => void;
    onSuccess?: () => void;
}

export default function TrainPagesDialog({ 
    open, 
    websiteId, 
    pages, 
    onClose, 
    onSuccess 
}: TrainPagesDialogProps) {
    const { post } = useApi();
    const { captureEvent } = useAnalytics();
    const [selectedPages, setSelectedPages] = React.useState<Set<string>>(new Set());
    const [trainingPages, setTrainingPages] = React.useState<boolean>(false);

    // Initialize selected pages when dialog opens
    React.useEffect(() => {
        if (open && pages.length > 0) {
            setSelectedPages(new Set(pages.map(page => page.url)));
        }
    }, [open, pages]);

    const togglePageSelection = (url: string) => {
        setSelectedPages(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(url)) {
                newSelected.delete(url);
            } else {
                newSelected.add(url);
            }
            return newSelected;
        });
    };

    const selectAllPages = () => {
        setSelectedPages(new Set(pages.map(page => page.url)));
    };

    const deselectAllPages = () => {
        setSelectedPages(new Set());
    };

    const trainSelectedPages = async () => {
        if (selectedPages.size === 0) {
            toast.error('No pages selected for training', toastConfig);
            return;
        }

        setTrainingPages(true);
        try {
            const urls = Array.from(selectedPages);
            await post('/add-urls-to-index/', {
                website_id: websiteId,
                urls: urls
            });

            toast.success(`Training ${urls.length} page${urls.length > 1 ? 's' : ''} started`, toastConfig);
            captureEvent('train_not_trained_pages', {
                website_id: websiteId,
                page_count: urls.length
            });

            handleClose();
            onSuccess?.();

        } catch (error: any) {
            console.error('Error training pages:', error);
            toast.error(error.detail || 'Failed to train pages', toastConfig);
        } finally {
            setTrainingPages(false);
        }
    };

    const handleClose = () => {
        setSelectedPages(new Set());
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2 text-blue-500" />
                        Not Trained Pages
                    </DialogTitle>
                    <DialogDescription>
                        Select the pages you want to train. These pages will be added to the training queue.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center justify-between my-2">
                    <div className="space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={selectAllPages}
                            disabled={pages.length === 0}
                        >
                            Select All
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={deselectAllPages}
                            disabled={selectedPages.size === 0}
                        >
                            Deselect All
                        </Button>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {selectedPages.size} of {pages.length} selected
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto py-2">
                    {pages.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground">
                            No untrained pages found
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {pages.map((page) => (
                                <div
                                    key={page.id}
                                    className={`flex items-center p-2 border rounded-md ${selectedPages.has(page.url) ? 'border-border bg-accent/20' : ''
                                        }`}
                                >
                                    <Checkbox
                                        id={`page-${page.id}`}
                                        checked={selectedPages.has(page.url)}
                                        onCheckedChange={() => togglePageSelection(page.url)}
                                        className="mr-2"
                                    />
                                    <div className="flex items-center space-x-2 overflow-hidden flex-1">
                                        <File className="w-4 h-4 flex-shrink-0" />
                                        <div className="truncate text-sm flex-1 max-w-[300px] md:max-w-[400px] lg:max-w-[500px]">{page.url}</div>
                                        {page.indexed_at && (
                                            <div className="text-xs text-muted-foreground whitespace-nowrap">
                                                <Clock className="w-3 h-3 inline mr-1" />
                                                {formatRelativeTime(page.indexed_at)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter className="mt-4">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="default"
                        onClick={trainSelectedPages}
                        disabled={selectedPages.size === 0 || trainingPages}
                        className="ml-2"
                    >
                        {trainingPages ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Training...
                            </>
                        ) : (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Train {selectedPages.size} Page{selectedPages.size !== 1 ? 's' : ''}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
} 